import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate as useRouterNavigate, generatePath } from 'react-router-dom';

import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard';
import FinancialDashboardNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import FinancialDashboardFormContainer from 'components/Common/FinancialDashboardFormContainer';
import { IconName, VisualType } from 'components/FinancialCheckup/FinancialDashboard/UpcomingSection/useUpcomingData';
import UpcomingItem from 'components/FinancialCheckup/FinancialDashboard/UpcomingSection/UpcomingItem/UpcomingItem';
import LoanProgressCard from 'components/FinancialCheckup/FinancialDashboard/Loan/LoanProgressCard';
import LoanHistory from 'components/FinancialCheckup/FinancialDashboard/Loan/LoansHistory';
import Skeleton from 'components/Common/Skeleton';

import { FlowComponentType } from 'routes/types';
import { getLoanBreakdown as getLoanBreakdownThunk } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { formatShortDate } from 'utils/dateUtils';
import { RoutePath } from 'enums/Routes';

import styles from './YourLoans.module.scss';

const YourLoans = ({ navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const navigate = useRouterNavigate();
  const { application } = useSelector(getApplicationData);
  const { breakdowns, isLoading } = useSelector(getLoanBreakdown);

  useEffect(() => {
    if (application?.id && breakdowns.length === 0 && !isLoading) {
      dispatch(getLoanBreakdownThunk(application.id));
    }
  }, [application]);

  const activeLoans = breakdowns.filter((loan) => loan.nextPaymentDate);
  const paidOffLoans = breakdowns.filter((loan) => !loan.nextPaymentDate);

  // Calculate totals from active loans only
  const totalPrincipalBalance = activeLoans.reduce((sum, loan) => sum + loan.principalBalance, 0);
  const totalLoanAmount = activeLoans.reduce((sum, loan) => sum + loan.totalLoanAmount, 0);
  const totalPeriodsRemaining = activeLoans.reduce((sum, loan) => sum + loan.periodsRemaining, 0);
  const totalPaymentsMade = activeLoans.reduce((sum, loan) => sum + (loan.totalPayments - loan.periodsRemaining), 0);
  const totalPayments = activeLoans.reduce((sum, loan) => sum + loan.totalPayments, 0);

  return (
    <>
      <FinancialDashboardNavigation {...navigationInfo} />
      <FinancialDashboardFormContainer
        title={
          isLoading || breakdowns.length === 0 ? (
            <Skeleton width="180px" height="48px" />
          ) : (
            formatMonetaryAmount(totalPrincipalBalance, true)
          )
        }
        subtitle="Remaining balance"
      >
        <LoanProgressCard
          isLoading={isLoading || breakdowns.length === 0}
          periodsRemaining={totalPeriodsRemaining}
          principalBalance={totalPrincipalBalance}
          totalLoanAmount={totalLoanAmount}
          paymentsMade={totalPaymentsMade}
          totalPayments={totalPayments}
        />

        {(activeLoans.length > 0 || isLoading) && (
          <div className={styles.section}>
            <p className={styles.sectionTitle}>Manage your loans</p>

            <ActionCard variant={ActionCardVariant.White} contentClassName={styles.actionCardContent}>
              {isLoading || breakdowns.length === 0 ? (
                <div className={styles.manageYourLoansSkeleton}>
                  <div className={styles.manageYourLoansSkeletonItem}>
                    <Skeleton width="30%" height="22px" />
                    <Skeleton width="40%" height="20px" />
                  </div>
                  <Skeleton width="30%" height="22px" />
                </div>
              ) : (
                <>
                  {[...activeLoans]
                    .sort((a, b) => new Date(a.dateOfLoan).getTime() - new Date(b.dateOfLoan).getTime())
                    .map((loan, index) => (
                      <>
                        {index > 0 && <div className={styles.separator} />}
                        <ActionCard.Col key={loan.id} className={styles.loanDetailsContainer}>
                          <ActionCard.Row>
                            <UpcomingItem
                              item={{
                                title: `Loan ${index + 1}`,
                                description: `Funded on ${formatShortDate(new Date(loan.dateOfLoan))}`,
                                value: formatMonetaryAmount(loan.principalBalance, true),
                                visual: { type: VisualType.Icon, name: IconName.Settings },
                                onClick: () =>
                                  navigate(generatePath(RoutePath.ManageYourLoan, { loanId: loan.id.toString() })),
                              }}
                            />
                          </ActionCard.Row>
                        </ActionCard.Col>
                      </>
                    ))}
                </>
              )}
            </ActionCard>
          </div>
        )}

        {!isLoading && paidOffLoans.length > 0 && <LoanHistory count={paidOffLoans.length} title="Loan history" />}
      </FinancialDashboardFormContainer>
    </>
  );
};

export default YourLoans;
